import type { ReactNode } from 'react'
import { settingsCopy } from '@/copy'
import { Avatar } from '@/ui/Avatar/Avatar'
import { Chip } from '@/ui/Chip/Chip'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { RowListItem } from '@/ui/RowList/RowList'
import { lifecycleTone } from '@/ui/Status/lifecycle'
import { StatusDot } from '@/ui/Status/Status'
import type { AgentDefInfo } from '../types/api'
import styles from './AgentRow.module.css'

/**
 * One agent row for a `RowList`: avatar, name and sub-line, model, status. A
 * pending pause, restore or delete is in progress, so it reads as
 * `reconciling` (D192).
 */
export function AgentRow({
  agent,
  pendingLabel,
  actions,
}: {
  agent: AgentDefInfo
  pendingLabel?: string | null
  actions?: ReactNode
}) {
  const statusLabel = agent.is_running
    ? settingsCopy.agents.running
    : agent.status
  return (
    <RowListItem>
      <Avatar name={agent.name} kind="agent" size="md" />
      <Stack gap="space-4" className={styles.main}>
        <Text>{agent.name}</Text>
        {agent.description ? (
          <Text variant="caption" tone="text-mute">
            {agent.description}
          </Text>
        ) : (
          <Text variant="code" tone="text-mute">
            {agent.agent_id}
          </Text>
        )}
        {pendingLabel && (
          <Text tone="text-sub" role="status">
            <StatusDot
              tone={lifecycleTone('reconciling')}
              label={pendingLabel}
            />
          </Text>
        )}
      </Stack>
      <Chip variant="kind" label={agent.model} />
      <Text tone="text-sub">
        <StatusDot tone={lifecycleTone(statusLabel)} label={statusLabel} />
      </Text>
      {actions && (
        <Inline gap="space-4" wrap={false}>
          {actions}
        </Inline>
      )}
    </RowListItem>
  )
}
